type SectionStatus = 'complete' | 'in-progress' | 'not-started';

interface SectionMeta {
  id: string;
  title: string;
  index: number;
  description: string;
  status: SectionStatus;
}

interface SubgroupRates {
  white: number;
  hispanic_latino: number;
  black: number;
  iep: number;
  multilingual_learners: number;
  female: number;
  male: number;
}

interface Prototype1Facts {
  district: { name: string; cohort: string };
  cohort_size: number;
  eligibility: {
    overall_pct: number;
    eligible_count: number;
    statewide_avg_pct: number;
    by_subgroup: SubgroupRates;
  };
  postsecondary_readiness: {
    overall_pct: number;
    by_route: { career_only: number; both: number; college_only: number };
    by_subgroup: SubgroupRates;
  };
}

const SECTIONS: SectionMeta[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    index: 1,
    description: 'Overview of the EOAA audit process and district background.',
    status: 'complete',
  },
  {
    id: 'college-eligibility',
    title: 'College Eligibility',
    index: 2,
    description: 'Analysis of college eligibility rates across student demographics.',
    status: 'not-started',
  },
  {
    id: 'postsecondary-readiness',
    title: 'Postsecondary Readiness',
    index: 3,
    description: 'Evaluation of postsecondary readiness indicators and trends.',
    status: 'in-progress',
  },
  {
    id: 'stakeholder-perceptions',
    title: 'Stakeholder Perceptions',
    index: 4,
    description: 'Survey results and analysis of stakeholder feedback on equity.',
    status: 'not-started',
  },
  {
    id: 'student-experience',
    title: 'Student Experience',
    index: 5,
    description: 'Student interviews, focus groups, and experience data analysis.',
    status: 'not-started',
  },
  {
    id: 'recommendations',
    title: 'Recommendations',
    index: 6,
    description: 'Actionable recommendations for improving equity outcomes.',
    status: 'not-started',
  },
  {
    id: 'executive-summary',
    title: 'Executive Summary',
    index: 7,
    description: 'High-level summary of findings and key takeaways for leadership.',
    status: 'not-started',
  },
];

const FACTS: Prototype1Facts = {
  district: { name: 'Issaquah School District', cohort: 'Class of 2025' },
  cohort_size: 2310,
  eligibility: {
    overall_pct: 54,
    eligible_count: 1247,
    statewide_avg_pct: 51,
    by_subgroup: {
      white: 63,
      hispanic_latino: 34,
      black: 37,
      iep: 19,
      multilingual_learners: 28,
      female: 57,
      male: 51,
    },
  },
  postsecondary_readiness: {
    overall_pct: 42,
    by_route: { career_only: 68, both: 22, college_only: 10 },
    by_subgroup: {
      white: 58,
      hispanic_latino: 29,
      black: 31,
      iep: 18,
      multilingual_learners: 24,
      female: 0,
      male: 0,
    },
  },
};

const INITIAL_DRAFTS: Record<string, string> = {
  introduction: `During the spring of 2025, Issaquah School District (Issaquah) collaborated with the XQ Institute (XQ) to examine the high school experience. This was accomplished by undertaking an Educational Outcomes Audit & Analysis (EOAA), which brought together educators, students, families, and community members to envision a high school experience more relevant and engaging so that all students graduate prepared to thrive in a rapidly changing world.

XQ is committed to supporting Issaquah as it strives to prepare every student for success in college, career, and life.

## Overview of XQ's Educational Outcomes Audit & Analysis (EOAA)

XQ's EOAA examines the journeys that students take through high school, identifying hidden patterns of college eligibility and postsecondary readiness.

The analysis uses a range of information to examine how students prepare for college and career on their journey through high school. It gathers data from three sources: quantitative course enrollment and completion data, stakeholder perception surveys, and qualitative student experience research.

With this data, XQ generates rigorous and accessible reports for each school that are used by teams to capture and prioritize findings, generate ideas to reduce barriers to student success, and map out next steps for improvement.`,
  'postsecondary-readiness': `Postsecondary readiness is defined as meeting the threshold for either the college-ready route, the career-prepared route, or both. In Issaquah School District, 42% of students in the Class of 2025 cohort met at least one postsecondary readiness benchmark — a rate that reflects both strengths in career pathway programming and significant gaps in college-readiness indicators.

Among postsecondary-ready students, the majority (68%) qualified through the career-prepared route alone, while 22% met both college-ready and career-prepared benchmarks. Only 10% qualified exclusively via the college-ready route, underscoring the district's relative strength in CTE programming compared to academic intensity metrics.

Significant disparities persist across student subgroups. While 58% of White students met postsecondary readiness benchmarks, only 29% of Hispanic/Latino students and 31% of Black/African American students achieved the same threshold. Students with IEPs had the lowest readiness rate at 18%, pointing to a critical need for targeted intervention strategies.

Multilingual learners showed a readiness rate of 24%, though notably their career-prepared rate (19%) outpaced their college-ready rate (7%), suggesting that CTE pathways may serve as a more accessible on-ramp for this population.`,
};

const INITIAL_SECTION_STATUSES: Record<string, SectionStatus> = Object.fromEntries(
  SECTIONS.map((s) => [s.id, s.status])
);

interface SeedAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

const INITIAL_CHAT_HISTORIES_BY_SECTION: Record<string, SeedAssistantMessage[]> = {};

const DRAFT_BEGIN_MARKER = '<<<DRAFT_BEGIN>>>';
const DRAFT_END_MARKER = '<<<DRAFT_END>>>';

const DRAFT_BEGIN_RE = /<{2,3}\s*DRAFT_BEGIN\s*(?::\s*([a-z0-9-_]+)\s*)?>{2,3}/i;
const DRAFT_END_RE = /<{2,3}\s*DRAFT_END\s*>{2,3}/i;
const APPROVE_RE = /<{2,3}\s*APPROVE\s*(?::\s*([a-z0-9-_]+)\s*)?>{2,3}/i;
const APPROVE_GLOBAL_RE = /<{2,3}\s*APPROVE\s*(?::\s*[a-z0-9-_]+\s*)?>{2,3}/gi;
const MARKER_GLOBAL_RE = /<{2,3}\s*[A-Z_]+\s*(?::\s*[a-z0-9-_]+\s*)?>{2,3}/gi;

interface ParsedDraft {
  sectionId?: string;
  body: string;
}

interface ParsedApprove {
  sectionId?: string;
}

interface ParsedChatResponse {
  chat: string;
  draft?: ParsedDraft;
  approve?: ParsedApprove;
}

const normalizeSectionId = (raw?: string): string | undefined => {
  if (!raw?.trim()) return undefined;
  const trimmed = raw.trim();
  const slug = trimmed.toLowerCase().replace(/[\s_]+/g, '-');
  const byId = SECTIONS.find((s) => s.id === slug || s.id === trimmed);
  if (byId) return byId.id;
  const byTitle = SECTIONS.find((s) => s.title.toLowerCase() === trimmed.toLowerCase());
  return byTitle?.id;
};

const looksLikeSectionDraft = (text: string): boolean => {
  const trimmed = text.trim();
  if (trimmed.length < 80) return false;
  if (/draft_begin/i.test(trimmed)) return false;
  return /^#{1,3}\s/m.test(trimmed) || trimmed.split('\n\n').filter(Boolean).length >= 2;
};

const parseChatResponse = (raw: string): ParsedChatResponse => {
  let text = raw ?? '';
  let draft: ParsedDraft | undefined;

  const beginMatch = text.match(DRAFT_BEGIN_RE);
  if (beginMatch && beginMatch.index !== undefined) {
    const beginIdx = beginMatch.index;
    const afterBegin = beginIdx + beginMatch[0].length;
    const tail = text.slice(afterBegin);
    const endMatch = tail.match(DRAFT_END_RE);
    const endRel = endMatch?.index;
    const body = endRel === undefined ? tail : tail.slice(0, endRel);
    const trimmedBody = body.trim();
    if (trimmedBody) {
      draft = { sectionId: normalizeSectionId(beginMatch[1]) ?? beginMatch[1], body: trimmedBody };
    }
    const endAbs = endRel === undefined ? text.length : afterBegin + endRel + (endMatch?.[0].length ?? 0);
    text = `${text.slice(0, beginIdx)}\n${text.slice(endAbs)}`;
  }

  if (!draft) {
    const fenceMatch = text.match(/```(?:markdown|md)?\s*\n([\s\S]*?)```/i);
    if (fenceMatch) {
      const fencedBody = fenceMatch[1]?.trim();
      if (fencedBody && looksLikeSectionDraft(fencedBody)) {
        draft = { body: fencedBody };
        text = text.replace(fenceMatch[0], '');
      }
    }
  }

  const approveMatch = text.match(APPROVE_RE);
  const approve: ParsedApprove | undefined = approveMatch
    ? { sectionId: normalizeSectionId(approveMatch[1]) ?? approveMatch[1] }
    : undefined;
  if (approve) text = text.replace(APPROVE_GLOBAL_RE, '');

  const chat = text
    .replace(MARKER_GLOBAL_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { chat, draft, approve };
};

export {
  SECTIONS,
  FACTS,
  INITIAL_DRAFTS,
  INITIAL_SECTION_STATUSES,
  INITIAL_CHAT_HISTORIES_BY_SECTION,
  parseChatResponse,
  DRAFT_BEGIN_MARKER,
  DRAFT_END_MARKER,
};
export type { SectionMeta, SectionStatus };
